import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { MatSelectChange } from "@angular/material/select";
import { Router } from "@angular/router";
import { CKEditorComponent } from "@ckeditor/ckeditor5-angular";
import { ClassicEditor } from "ckeditor5";
import {catchError, finalize, tap, throwError} from "rxjs";
import { FileInfo } from "src/app/models/docket";
import { EPDSFile } from "src/app/models/file-upload";
import { AddIntervenorResponse } from "src/app/models/parties";
import { CKEditorConfig, DocInfo, InterpolationDetails } from "src/app/models/template";
import { UserRoleString } from "src/app/models/user";
import { FileUploadService } from "src/app/services/file-upload.service";
import { HttpService } from "src/app/services/http.service";
import { StateService } from "src/app/services/state.service";
import { SubmitNewDocService } from "src/app/services/submit-new-doc.service";
import { TemplateService } from "src/app/services/template.service";
import { UtilService } from "src/app/services/util.service";
import { DenialDoc } from "../docket-document-view.component";
import {FileUploadComponent} from "../../file-upload/file-upload.component";

interface DenialDocRequest {
  comments?: string;
  denialDocInfo: {
    accessType: string;
    approve: string;
    fileId: number;
  };
  docId: number;
  isDocConfidential: string;
  protestId: string;
  requestType: string;
  typeofdocument: string;
  content?: string;
}

@Component({
  selector: 'app-denial-of-document',
  templateUrl: './denial-of-document.component.html',
  styleUrl: './denial-of-document.component.scss',
  standalone: false
})
export class DenialOfDocumentComponent implements OnInit {
  @ViewChild('fileUpload', { static: false }) fileUpload!: FileUploadComponent;
  @Input() fileInfo!: FileInfo;
  @Input() set denialDocInfoList(value: Array<DenialDoc>) {
    if (value) {
      this.selectedDocInfo = value[0];
    }
  }

  @Output() onSubmitDenialDoc: EventEmitter<AddIntervenorResponse> = new EventEmitter<AddIntervenorResponse>();
  editorContent: any;
  attorneyInfoPlaceholder: any;
  isEditorVisible: boolean = false;
  comments: string = '';
  templateSaving = false;
  protected readonly ClassicEditor = ClassicEditor;
  protected readonly CKEditorConfig = CKEditorConfig;
  uploadedFiles: EPDSFile[] = [];
  fileUploadInProgress = false;
  requiredFileMessage = 'Upload Document (' + this.fileUploadService.udmPdfWordExcelZip + ')';
  requiredFileType = this.fileUploadService.rftPdfWordExcelZip;
  attachAssociatedDocOutput = false;

  selectedDocInfo!: DenialDoc;
  isReviewVisible: boolean = false;
  generatedDocFileName?: string;

  isGenTemplateValid = false
  isUploadValid = false;
  isFormValid = false;

  ngOnInit(): void {

  }

  @ViewChild('editor', { static: false }) editor!: CKEditorComponent;

  constructor(private httpService: HttpService,
              private readonly utilService: UtilService,
              private readonly router: Router,
              private readonly stateService: StateService,
              private readonly templateService: TemplateService,
              private readonly fileUploadService: FileUploadService,
              private submitNewDocService: SubmitNewDocService
  ) {
  }

  onGenerateTemplate() {
    if (!this.selectedDocInfo || !this.fileInfo.a_No) return;

    this.templateService.generateTemplateBasedOnDocumentType(this.selectedDocInfo.doc_Type_Id, this.fileInfo.a_No)
      .subscribe(templateDetails => {
        console.debug(templateDetails);
        this.editorContent = templateDetails.htmlContent;

        const todaysDate = new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        const reportDueDate = templateDetails.reportDueDate || '';
        const attorneyInfo = templateDetails.attorneyInfo || this.attorneyInfoPlaceholder;

        const templateInput: InterpolationDetails = {
          protestInfo: this.sanitizeObject(templateDetails.protestInfo),
          attorneyInfo: this.sanitizeObject(attorneyInfo),
          todaysDate,
          reportDueDate
        };

        this.interpolateUsingEvalWIthJavascriptTemplateLiterals(templateInput);
        this.isEditorVisible = true;
      });
  }

  private sanitizeObject(obj: any): any {
    if (!obj) return {};
    return Object.keys(obj).reduce((sanitized, key) => {
      sanitized[key] = obj[key] ?? '';
      return sanitized;
    }, {} as any);
  }

  interpolateUsingEvalWIthJavascriptTemplateLiterals(interpolationDetails: InterpolationDetails) {
    if (!this.editorContent) return;

    // Replace Angular-style interpolation {{ }} with JavaScript template literals ${ }
    const template = this.editorContent.replace(/{{/g, '${').replace(/}}/g, '}');

    this.editorContent = this.renderTemplate(template, interpolationDetails);
  }

  renderTemplate(templateStr: string, args: any): string {
    templateStr = templateStr.replace(/`/g, '\\`');

    const keys = Object.keys(args);
    const fn = new Function(...keys, 'return `' + templateStr + '`');

    return fn(...keys.map(key => args[key]));
  }

  onEditorSave() {
    this.editorContent = this.editor.editorInstance?.getData();
    if (this.editorContent && this.selectedDocInfo) {
      this.templateSaving = true;

      this.templateService.createPdf(this.editorContent, this.selectedDocInfo.doc_Type_Id)
        .pipe(finalize(() => this.templateSaving = false))
        .subscribe({
          next: (doc) => {
            this.isEditorVisible = false;
            this.generatedDocFileName = this.utilService.toCamelCase(this.selectedDocInfo?.doc_Type_Desc || doc.fileName) + '.pdf';
            this.isReviewVisible = true;
          },
          error: () => {
            this.utilService.displayDialogMessage("Error", "There was a problem saving the template updates.");
          }
        });
    } else {
      console.error(this.editorContent);
      this.utilService.displayDialogMessage("Error", "There was a problem saving the template updates.");
    }
  }

  onEditTemplate() {
    this.isEditorVisible = true;
  }

  onEditorCancel() {
    this.isEditorVisible = false;
  }

  onViewDocument() {
    const filename = this.generatedDocFileName;

    if (filename && (this.selectedDocInfo != undefined)) {
      this.templateService.downloadTempPdf(filename, this.selectedDocInfo?.doc_Type_Id);
    } else {
      this.utilService.displayDialogMessage("Error", "There was a problem generating the temporary PDF.");
    }
  }

  onClearEdits() {
    this.isReviewVisible = false;
    this.generatedDocFileName = undefined;
  }

  onEPDSFileListChange(epdsFileList: EPDSFile[]): void {
    this.uploadedFiles = epdsFileList;
  }

  onSubmit() {
    this.templateSaving = true;
    if (this.uploadedFiles.length > 0) {
      this.handleFileUpload();
    } else {
      this.submitDocInfo();
    }
  }

  private handleFileUpload() {
    const allFiles = this.uploadedFiles.map(epdsFile => ({
      epdsFile,
      attachmentType: this.selectedDocInfo.attachmentType
    }));

    this.fileUploadInProgress = true;
    this.fileUploadService.submitFile(allFiles, this.fileInfo.a_No, this.fileUpload).pipe(
      tap(resp => console.log('File upload response:', resp)),
      catchError(err => {
        // console.error('File upload error:', err);
        this.fileUploadInProgress = false;
        this.templateSaving = false;
        return throwError(err);
      }),
      finalize(() => this.fileUploadInProgress = false)
    ).subscribe(() => this.submitDocInfo());
  }

  submitDocInfo(): void {
    const requestType = 'other';
    const formData = this.getDenialDocRequest();
    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/add-attachments/' + requestType;

    this.httpService.Post<AddIntervenorResponse>(url, httpParams, options).subscribe({
      next: (response) => {
        console.log('Response:', response);
        if (response.submitNewDocDTO) {
          this.onSubmitDenialDoc.emit(response);
        }
        this.templateSaving = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.utilService.displayDialogMessage("Error", "There was a problem submitting the document.");
      }
    });
  }

  isValid(): boolean {
    this.updateValidationStates();
    return this.isFormValid;
  }

  private updateValidationStates(): void {
    this.isGenTemplateValid = this.isReviewVisible && !!this.generatedDocFileName;
    this.isUploadValid = this.uploadedFiles.length > 0;
    this.isFormValid = (this.isGenTemplateValid && !this.attachAssociatedDocOutput) || this.isUploadValid;
  }

  private getDenialDocRequest(): DenialDocRequest {
    const { accessType, doc_Type_Id, doc_Type_Desc } = this.selectedDocInfo;
    const baseRequest: DenialDocRequest = {
      comments: this.utilService.removeCurlyQuotes(this.comments),
      denialDocInfo: {
        accessType,
        approve: 'N',
        fileId: this.fileInfo.file_Id,
      },
      docId: doc_Type_Id,
      isDocConfidential: 'N',
      protestId: this.fileInfo.a_No,
      requestType: 'other',
      typeofdocument: doc_Type_Desc,
    };

    return this.generatedDocFileName ? { ...baseRequest, content: this.templateService.replaceChars(this.editorContent) } : baseRequest;
  }
}
